import React, { useEffect, useState } from "react";
import { Col, Row, Form, Modal } from "react-bootstrap";
import PropTypes from 'prop-types';
import { Button } from "@mui/material";
import { object, string } from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { TextInput } from '../../Helpers/Inputs';
import SwitchButtons from '../../Quoters/Common/SubComponents/SwitchButtons';
import { NumberFormatDollars, NumberFormatNumeric } from '../../Helpers/Formatters';
import CurrencyFormat from '../../Helpers/Formatters/CurrencyFormat';
import { SESSION_STORAGE_BUSINESS_SOLUTIONS_KEYPERSON_COVERAGE_KEY } from "../../../sessionStorage";
import './KeyPersonCoverageModal.scss';

const REPLACE_METHODS = {
    GENERAL: 'General lost profits',
    SPECIFIC: 'Specific lost profits',
    RECRUITMENT: 'Lost profits & recruitment'
};

export default function KeyPersonCoverageModal(props) {
    const [formValues, setFormValues] = useState(KeyPersonCoverageModal.DefaultFormState);
    const [selectedCard, setSelectedCard] = useState(null);
    const [calcAmounts, setCalcAmounts] = useState({ income: 0, lostProfit: 0, recruitment: 0 });
    const max9DigitValue = 999999999;
    const max10DigitValue = 9999999999;

    const formMethods = useForm({
        mode: 'all',
        reValidateMode: 'all',
        defaultValues: formValues,
    });

    const { handleSubmit, setValue } = formMethods;

    // Helper to safely parse formatted currency inputs
    const parseMoney = (val) => parseFloat(val?.toString().replace(/[^0-9.-]+/g, "")) || 0;

    // Calculate Math ONLY for the currently active tab & Retain previous calculations for inactive tabs.
    useEffect(() => {
        const valExisting = formValues.hasExisting === 'Yes' ? parseMoney(formValues.existingCoverage) : 0;
        const valIncome = parseMoney(formValues.income);
        const valRevenue = parseMoney(formValues.revenue);
        const valYears = Number(formValues.yearsToReplace) || 0;
        const valRecruiting = parseMoney(formValues.recruitingCost);
        const valTraining = parseMoney(formValues.trainingCost);
        const valCushion = parseMoney(formValues.cushion);

        // If 'Yes' is selected, the existingCoverage field MUST be filled to be considered complete.
        const isExistingComplete = formValues.hasExisting === 'No' || (formValues.hasExisting === 'Yes' && formValues.existingCoverage !== '');

        setCalcAmounts(prevAmounts => {
            const newAmounts = { ...prevAmounts }; // Copy previous values so they aren't lost on tab switch

            if (formValues.replaceMethod === REPLACE_METHODS.GENERAL) {
                const isIncomeComplete = formValues.income !== '' && isExistingComplete;
                newAmounts.income = isIncomeComplete ? Math.round(Math.max(0, (valIncome * 10) - valExisting)) : 0;
            }

            if (formValues.replaceMethod === REPLACE_METHODS.SPECIFIC) {
                const isSpecificComplete = formValues.revenue !== '' && formValues.yearsToReplace !== '' && isExistingComplete;
                newAmounts.lostProfit = isSpecificComplete ? Math.round(Math.max(0, (valRevenue * valYears) - valExisting)) : 0;
            }

            if (formValues.replaceMethod === REPLACE_METHODS.RECRUITMENT) {
                const isRecruitmentComplete =
                    formValues.revenue !== '' &&
                    formValues.yearsToReplace !== '' &&
                    formValues.recruitingCost !== '' &&
                    formValues.trainingCost !== '' &&
                    formValues.cushion !== '' &&
                    isExistingComplete;

                newAmounts.recruitment = isRecruitmentComplete
                    ? Math.round(Math.max(0, (valRevenue * valYears) + valRecruiting + valTraining + valCushion - valExisting))
                    : 0;
            }

            if (
                prevAmounts.income === newAmounts.income &&
                prevAmounts.lostProfit === newAmounts.lostProfit &&
                prevAmounts.recruitment === newAmounts.recruitment
            ) {
                return prevAmounts;
            }

            return newAmounts;
        });

    }, [formValues]);

    // Auto-deselect if calculation drops to zero
    useEffect(() => {
        if (selectedCard === 'income' && calcAmounts.income === 0) setSelectedCard(null);
        if (selectedCard === 'lostProfit' && calcAmounts.lostProfit === 0) setSelectedCard(null);
        if (selectedCard === 'recruitment' && calcAmounts.recruitment === 0) setSelectedCard(null);
    }, [calcAmounts, selectedCard]);

    const handleInputChange = (fieldName, value) => {
        if (formValues[fieldName] === value) return;

        const formStateToUpdate = { ...formValues };

        // Clear existing coverage if toggled to No
        if (fieldName === 'hasExisting' && value === 'No') {
            formStateToUpdate.existingCoverage = '';
            setValue('existingCoverage', ''); // Keep React Hook Form in sync
        }

        formStateToUpdate[fieldName] = value;
        setFormValues(formStateToUpdate);

        setValue(fieldName, value); // Sync React Hook Form's internal state
    };

    const routeChange = () => {
        const selectedValue = calcAmounts[selectedCard];

        const methodNames = {
            income: 'Income Multiplier',
            lostProfit: 'Lost Profit',
            recruitment: 'Lost Profit & Recruitment'
        };

        const sessionPayload = {
            method: methodNames[selectedCard],
            amount: selectedValue
        };

        sessionStorage.setItem(SESSION_STORAGE_BUSINESS_SOLUTIONS_KEYPERSON_COVERAGE_KEY, JSON.stringify(sessionPayload));
        props.onClose();
        window.location.href = '/landing/term-insurance';
    };

    const renderCard = (id, label, amount) => (
        <Col sm={4} className="mb-3 px-2 w-100" key={id}>
            <div
                className={`p-3 text-center bg-white d-flex flex-column h-100 comparison-card ${selectedCard === id ? 'selected' : ''} ${amount === 0 ? 'disabled' : ''}`}
                onClick={() => amount > 0 && setSelectedCard(id)}
            >
                <div className="mt-auto px-1">
                    <p className="mb-2 fw-bold text-uppercase card-label">
                        {label}
                    </p>
                    <h4 className="fw-bold m-0 text-break">
                        <CurrencyFormat whole number={amount} className="card-rate" />
                    </h4>
                </div>
                <div className={`mt-auto card-radio-btn ${selectedCard === id ? 'selected' : ''}`} />
            </div>
        </Col>
    );

    const renderField = (name, label, colLg = 6, isCurrency = true, hint = null, inputType = "text", range = undefined) => (
        <Col lg={colLg} className="w-100 mb-3" key={name}>
            <TextInput
                name={name}
                id={name}
                label={label}
                type={inputType}
                value={formValues[name]}
                onChange={(e) => handleInputChange(name, e.target.value)}
                InputProps={isCurrency ? { inputComponent: NumberFormatDollars } : name === 'yearsToReplace' ? { inputComponent: NumberFormatNumeric } : undefined}
                inputProps={range ? {
                    min: range?.min,
                    max: range?.max,
                    maxvalue: range?.maxvalue,
                } : undefined}
            />
            {hint ? (
                <small className="text-muted d-block mt-1">{hint}</small>
            ) : null}
        </Col>
    );

    return (
        <Modal
            show={props.open}
            onHide={props.onClose}
            size="lg"
            centered
            dialogClassName="keyperson-modal"
        >
            <Modal.Header closeButton className="border-bottom-0 modal-header-custom">
                <div className="w-100">
                    <p className="mb-0 header-subtitle fw-medium">
                        Key Person Coverage
                    </p>
                    <h4 className="mb-0 fw-bold">Calculate your coverage amount</h4>
                </div>
            </Modal.Header>
            <Modal.Body className="modal-scroll p-4 modal-body-custom">
                <Row className="mb-4 justify-content-center align-items-stretch m-auto">
                    {renderCard('income', 'Income Multiplier', calcAmounts.income)}
                    {renderCard('lostProfit', 'Lost Profit', calcAmounts.lostProfit)}
                    {renderCard('recruitment', 'Lost Profit & Recruitment', calcAmounts.recruitment)}
                </Row>

                <FormProvider {...formMethods}>
                    <Form onSubmit={handleSubmit(routeChange)}>
                        <div className="mb-4">
                            <p className="mb-2 text-dark fw-medium" style={{ fontSize: '0.9rem' }}>
                                What are you looking to replace?
                            </p>
                            <ul className="nav nav-tabs w-100 d-flex flex-row replace-method-tabs">
                                {Object.values(REPLACE_METHODS).map(method => (
                                    <li className="nav-item flex-fill text-center" key={method}>
                                        <button
                                            type="button"
                                            className={`nav-link replace-method-tab-btn ${formValues.replaceMethod === method ? 'selected' : ''}`}
                                            onClick={() => handleInputChange('replaceMethod', method)}
                                        >
                                            {method}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                            {/* Hidden input to ensure RHF maps the replaceMethod properly if needed */}
                            <input type="hidden" {...formMethods.register('replaceMethod')} />
                        </div>

                        {/* Only show the remaining fields if a replace method tab has been selected */}
                        {formValues.replaceMethod !== '' && (
                            <>
                                {/* Existing Coverage Toggle */}
                                <div className="mb-0 d-flex align-items-center flex-wrap gap-lg-5">
                                    <p className="mb-3 text-dark" style={{ fontSize: '0.9rem' }}>
                                        Do you have existing keyperson coverage for this individual?
                                    </p>
                                    <Col xs={12} sm={6} lg={4} className="px-0">
                                        <SwitchButtons
                                            name='hasExisting'
                                            selectedValue={formValues.hasExisting}
                                            buttonOne={{
                                                name: 'Yes',
                                                onClick: () => handleInputChange('hasExisting', 'Yes'),
                                                selectedValue: 'Yes'
                                            }}
                                            buttonTwo={{
                                                name: 'No',
                                                onClick: () => handleInputChange('hasExisting', 'No'),
                                                selectedValue: 'No'
                                            }}
                                            className="marriedSwitchButtons"
                                        />
                                    </Col>
                                </div>

                                <Row>
                                    {/* Existing Coverage Amount */}
                                    {formValues.hasExisting === 'Yes' && renderField('existingCoverage', 'Existing coverage', 12, true, null, "text", { maxvalue: max9DigitValue })}

                                    {/* General Lost Profits Fields */}
                                    {formValues.replaceMethod === REPLACE_METHODS.GENERAL && renderField('income', 'Keyperson annual income', 12, true, 'Used to estimate a 10x income replacement value.', "text", { maxvalue: max10DigitValue })}

                                    {/* Specific & Recruitment Shared Fields */}
                                    {(formValues.replaceMethod === REPLACE_METHODS.SPECIFIC || formValues.replaceMethod === REPLACE_METHODS.RECRUITMENT) && (
                                        <React.Fragment>
                                            {renderField('revenue', "Individual's annual revenue contribution", 6, true, null, "text", { maxvalue: max10DigitValue })}
                                            {renderField('yearsToReplace', 'Years to replace', 6, false, null, 'number', { min: 1, max: 5 })}
                                        </React.Fragment>
                                    )}

                                    {/* Recruitment Specific Fields */}
                                    {formValues.replaceMethod === REPLACE_METHODS.RECRUITMENT && (
                                        <React.Fragment>
                                            {renderField('recruitingCost', 'Recruiting cost', 6, true, null, "text", { maxvalue: max9DigitValue })}
                                            {renderField('trainingCost', 'Training cost', 6, true, null, "text", { maxvalue: max9DigitValue })}
                                            {renderField('cushion', 'Cushion', 6, true, null, "text", { maxvalue: max9DigitValue })}
                                        </React.Fragment>
                                    )}
                                </Row>
                            </>
                        )}

                        <div className="mt-2 pt-4 border-top d-flex align-items-center justify-content-between">
                            <span className="text-muted" style={{ fontSize: '0.9rem' }}>
                                {selectedCard
                                    ? `Coverage selected: $${calcAmounts[selectedCard].toLocaleString()}`
                                    : formValues.replaceMethod !== ''
                                        ? 'Complete the fields above, then choose a coverage amount to continue.'
                                        : 'Answer the question above to get started.'}
                            </span>
                            <Button
                                disabled={!selectedCard}
                                type="submit"
                                color="primary"
                                variant="contained"
                                size="large"
                                className={`get-rate-btn ${selectedCard ? 'selected' : ''}`}
                            >
                                GET MY RATE
                            </Button>
                        </div>
                    </Form>
                </FormProvider>
            </Modal.Body>
        </Modal>
    );
}

KeyPersonCoverageModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};

KeyPersonCoverageModal.DefaultFormState = {
    replaceMethod: '',
    hasExisting: '',
    existingCoverage: '',
    income: '',
    revenue: '',
    yearsToReplace: '',
    recruitingCost: '',
    trainingCost: '',
    cushion: ''
};
